import React, { useEffect, useRef, useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Button, { IconButton } from '@atlaskit/button/new';
import Lozenge from '@atlaskit/lozenge';
import PremiumIcon from '@atlaskit/icon/core/premium';

import SectionMessage from '@atlaskit/section-message';
import AIContainer from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-container/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { useRectObserver } from '@atlassian/jira-react-use-resize-observer/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { AiFooter } from './ui/footer/index.tsx';
import { StreamingStatusBar } from './ui/streaming-status-bar/index.tsx';
import { ToggleButtonContent } from './ui/toggle-button-content/index.tsx';
import type {
	ResourceSuggestion,
	RelatedIssuesSearchPayload,
	ComponentState,
	InitialStep,
	AiExperienceFeedbackForm,
	InvokedFrom,
} from './types.tsx';
import messages from './messages.tsx';

const PROACTIVE_CONTAINER_MINIMUM_WIDTH = 650;

const isSimilarWorkItemsUIFixesEnabled = (invokedFrom: InvokedFrom): boolean => {
	return invokedFrom === 'globalIssueCreate' && fg('similar_items_in_gic_ui_fixes');
};

/**
 * Props for the ProactiveContainer component that handles displaying AI-powered suggestions
 * @param children - React children to render
 * @param currentStep - Current state of the component ('FETCHING' | 'LOADED' | 'DISPLAY')
 * @param data - Array of related issues or resource suggestions
 * @param error - Any error that occurred during fetching
 * @param isBeta - Whether the feature is in beta
 * @param onFetchSuggestions - Callback to fetch suggestions
 * @param resultNumber - Number of results to display @deprecated - we will use data.length on FG cleanup
 * @param onDismissSuggestions - Callback when suggestions are dismissed (on unmount of component)
 * @param onDisplaySuggestions - Callback when suggestions are displayed
 * @param onLoadedSuggestions - Callback when suggestions are loaded
 * @param invokedFrom - Where the suggestions were invoked from
 * @param onAiInteractionInitiatedEvent - Analytics callback when AI interaction starts
 * @param onAiResultErrorEvent - Analytics callback when an error occurs
 * @param onAiResultViewedEventWhenCollapsed - Analytics callback when results are viewed in collapsed state
 * @param onAiResultViewedEventWhenExpanded - Analytics callback when results are viewed in expanded state
 * @param onAiResultActionedEvent - Analytics callback when a result is actioned
 * @param messages - Custom message overrides for various states
 * @param shouldReinitiate - Optional shouldReinitiate prop to trigger re-fetching
 */
type ProactiveProps = {
	children: React.ReactNode;
	initialStep: InitialStep;
	data: ResourceSuggestion[] | undefined;
	error: Error | unknown | undefined;
	loading: boolean;
	isBeta: boolean;
	feedbackCollectorId: string;
	feedbackFormEntrypointId: string;
	aiExperience: AiExperienceFeedbackForm;
	// When we move towards relay, we won't need a separate fetchSuggestions callback, but will have data and error passed in directly
	// and we'll have a Suspense fallback defined elsewhere for loading states
	onFetchSuggestions: (customRelatedIssueSearchPayload?: RelatedIssuesSearchPayload) => void;
	resultNumber?: number | undefined;
	onDismissSuggestions?: () => void;
	onDisplaySuggestions?: () => void;
	onLoadedSuggestions?: () => void;
	invokedFrom: InvokedFrom;
	// Analytic callbacks from consumer
	onAiInteractionInitiatedEvent: () => void;
	onAiResultErrorEvent: (error: unknown) => void;
	onAiResultViewedEventWhenCollapsed: () => void;
	onAiResultViewedEventWhenExpanded: () => void;
	onAiResultActionedEvent: () => void;
	onAiFeedbackSubmittedEvent: (aiFeedbackResult: 'up' | 'down', payload?: unknown) => void;
	// Optional message overrides
	messages: {
		headerTitle: MessageDescriptorV2;
		errorMessage: MessageDescriptorV2;
		noItemsMessage: MessageDescriptorV2;
		// To render a message with a number such as `Show 6 results`, please include {numResults} as a variable in the default message.
		// e.g `defaultMessage: 'Show {numResults} {numResults, plural, one {result} other {results}}'`
		// This will pluralise the message based on the number of results.
		viewItemsMessage?: MessageDescriptorV2;
		hideSuggestionsMessage?: MessageDescriptorV2;
		findingResultsMessage?: MessageDescriptorV2;
		inactiveMessage?: MessageDescriptorV2;
	};
	customRelatedIssueSearchPayload?: RelatedIssuesSearchPayload;
	headerNode?: React.ReactNode;
	// Add shouldReinitiate prop to trigger re-fetching
	shouldReinitiate?: number;
	setTriggerReinitiate?: () => void;
	footerActionNode: React.ReactNode;
	footerContainerStyles?: ReturnType<typeof xcss>;
};

export const ProactiveContainer = (props: ProactiveProps) => {
	const { formatMessage } = useIntl();

	const {
		data,
		loading,
		error,
		feedbackCollectorId,
		feedbackFormEntrypointId,
		invokedFrom,
		initialStep,
		aiExperience,
		onFetchSuggestions,
		onDisplaySuggestions,
		onLoadedSuggestions,
		onDismissSuggestions,
		children,
		isBeta,
		resultNumber,
		messages: customMessages,
		onAiInteractionInitiatedEvent,
		onAiResultErrorEvent,
		onAiResultViewedEventWhenCollapsed,
		onAiResultViewedEventWhenExpanded,
		onAiResultActionedEvent,
		onAiFeedbackSubmittedEvent,
		customRelatedIssueSearchPayload,
		headerNode,
		shouldReinitiate,
		setTriggerReinitiate,
		footerActionNode,
		footerContainerStyles,
	} = props;

	const [currentStep, setCurrentStep] = useState<ComponentState>(initialStep);

	// Add refs to track previous states
	const prevDataRef = useRef<typeof data>();
	const prevLoadingRef = useRef<boolean>(loading);
	const prevPayloadRef = useRef<RelatedIssuesSearchPayload | undefined>();
	const prevShouldReinitiateRef = useRef<number | undefined>(shouldReinitiate);

	const isFetching = currentStep === 'FETCHING' || loading;

	const shouldDisplay = currentStep === 'DISPLAY';

	const prevErrorRef = useRef<Error | unknown | undefined>(error);
	const prevIsDisabled =
		currentStep === 'INACTIVE'
			? false
			: isFetching || !data?.length || (error !== undefined && error !== null);

	// With the proactive container top nav refresh, we want to allow users to search again when there are no results or an error, and have
	// an additional button to view the error as well.
	const isError = error !== undefined && error !== null;
	const hasNoResults = fg('proactive-container-noresults-fix')
		? data && data.length === 0
		: !data?.length;
	const isErrorOrNoResults = !isFetching && (hasNoResults || isError);
	const isNotAbortError =
		isError && typeof error === 'object' && 'name' in error && error.name !== 'AbortError';

	// A rectObserver is used to track whether the proactive container is below a certain width - this allows us to render the subtext and buttons on a second row
	// to avoid crowding the UI and any text overflow.
	const containerRef = useRef<HTMLDivElement>(null);
	const containerRect = useRectObserver({ ref: containerRef });
	const isSmallWidth =
		containerRect?.width !== undefined && containerRect?.width < PROACTIVE_CONTAINER_MINIMUM_WIDTH;

	const isDisabled = prevIsDisabled && !isErrorOrNoResults;

	const componentState = useRef({
		isInitialized: false,
		lastInvokedFrom: invokedFrom,
		// Store dismissCallback in a ref, so it can be used in the cleanup effect on unmount only
		dismissCallback: onDismissSuggestions,
	});

	// Cleanup effect
	useEffect(() => {
		// Capture the current value of dismissCallback
		const currentDismissCallback = componentState.current.dismissCallback;

		return () => {
			currentDismissCallback?.();
		};
	}, []);

	// Component initialization effect - if inactive, don't fetch, otherwise fetch data and set current step to fetching
	useEffect(() => {
		if (shouldReinitiate !== prevShouldReinitiateRef.current) {
			componentState.current.isInitialized = false;
			prevShouldReinitiateRef.current = shouldReinitiate;
		}
		if (currentStep === 'INACTIVE') {
			return;
		}
		if (
			(!componentState.current.isInitialized && invokedFrom) ||
			componentState.current.lastInvokedFrom !== invokedFrom ||
			(!isEqual(prevPayloadRef.current, customRelatedIssueSearchPayload) &&
				expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false))
		) {
			componentState.current.isInitialized = true;
			componentState.current.lastInvokedFrom = invokedFrom;
			onFetchSuggestions(customRelatedIssueSearchPayload);
			setCurrentStep('FETCHING');
			onAiInteractionInitiatedEvent();
			if (expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false)) {
				prevPayloadRef.current = customRelatedIssueSearchPayload;
			}
		}
	}, [
		onFetchSuggestions,
		onAiInteractionInitiatedEvent,
		invokedFrom,
		customRelatedIssueSearchPayload,
		currentStep,
		shouldReinitiate,
	]);

	// Update the data effect to check for actual data changes and loading state changes
	useEffect(() => {
		if (error && error !== prevErrorRef.current) {
			prevErrorRef.current = error;
			onAiResultErrorEvent(error);
			// We should always fire an error event, but should allow users to enter the 'DISPLAY' step if they want to view the error section message
			// If a user receives an error while fetching, set it to the loading state. This must be conditional as we don't want the users to not be able to enter
			// the 'DISPLAY' state.
			if (isFetching) {
				setCurrentStep('LOADED');
				onLoadedSuggestions?.();
			}
			return;
		}

		/**
		 * State handling for fetching to ensure we use the loading state to determine whether to show the loading state
		 */
		const loadingChangedToFalse = prevLoadingRef.current === true && loading === false;
		prevLoadingRef.current = loading;

		/**
		 * If data has changed from before, or if loading has changed from true to false (meaning a fetch was initiated, and loading has completed)
		 * this handles the case where results show with an empty [] but has been fetched again (e.g. GIC)
		 */
		if ((data && !isEqual(prevDataRef.current, data)) || loadingChangedToFalse) {
			if (isFetching) {
				onLoadedSuggestions?.();
				if (fg('aix-1578-expand-proactive-on-improve-issue')) {
					// If the proactive container is invoked from the improve issue dropdown, we want to show the results in the expanded state
					// as the user is likely to want to view the results immediately.
					if (invokedFrom === 'improveIssueDropdown') {
						setCurrentStep('DISPLAY');
						onAiResultViewedEventWhenExpanded();
					} else {
						setCurrentStep('LOADED');
						onAiResultViewedEventWhenCollapsed();
					}
				} else {
					setCurrentStep('LOADED');
					onAiResultViewedEventWhenCollapsed();
				}
			}
			prevDataRef.current = data;
		}

		// If current step is DISPLAY and there are now no results, set the current step to LOADED
		if (currentStep === 'DISPLAY' && !data?.length) {
			// If there is an error (which also means there are no results), we still want users to be able to enter the 'DISPLAY' step
			// to view the error section message.
			if (!isError) {
				setCurrentStep('LOADED');
			}
		}
	}, [
		error,
		isError,
		data,
		loading,
		currentStep,
		onLoadedSuggestions,
		onAiResultViewedEventWhenCollapsed,
		onAiResultErrorEvent,
		isFetching,
		invokedFrom,
		onAiResultViewedEventWhenExpanded,
	]);

	/**
	 * Event handler for the toggle button click
	 * Since we use the same button for both the Pressable and the Button click, we need to stop the event propagation to not fire the event twice
	 */
	const onToggleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			if (isDisabled) {
				return;
			}
			if (currentStep === 'LOADED') {
				onDisplaySuggestions?.();
				setCurrentStep('DISPLAY');
				if (!isError) {
					onAiResultViewedEventWhenExpanded();
				}
			} else if (currentStep === 'DISPLAY') {
				onLoadedSuggestions?.();
				setCurrentStep('LOADED');
				onAiResultActionedEvent();
			} else if (currentStep === 'INACTIVE') {
				setCurrentStep('FETCHING');
			}
			e.stopPropagation();
		},
		[
			isDisabled,
			currentStep,
			onDisplaySuggestions,
			onAiResultViewedEventWhenExpanded,
			onLoadedSuggestions,
			onAiResultActionedEvent,
			isError,
		],
	);

	const renderActionButtons = () => {
		if (shouldDisplay) {
			return (
				<IconButton
					appearance="subtle"
					icon={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
					label={formatMessage(messages.hideSuggestions)}
					isDisabled={isDisabled}
					onClick={onToggleClick}
					isTooltipDisabled={false}
					spacing={isSmallWidth && isErrorOrNoResults ? 'compact' : 'default'}
					tooltip={{
						position: 'top',
					}}
				/>
			);
		}

		if (!isFetching) {
			return (
				<Inline space="space.050">
					{/* Render both "View error" and "Search again" buttons in the error case */}
					{isError && isNotAbortError && (
						<Button
							spacing="compact"
							onClick={onToggleClick}
							isDisabled={isDisabled}
							appearance="default"
							testId="ai-proactive-container.view-error-button"
						>
							<Text weight="medium" color="color.text">
								{formatMessage(messages.viewError)}
							</Text>
						</Button>
					)}

					{invokedFrom === 'globalIssueCreate' &&
					hasNoResults &&
					!isError &&
					!isNotAbortError &&
					fg('similar_items_in_gic_ui_fixes') ? null : (
						<Button
							spacing="compact"
							onClick={hasNoResults ? onReinitiateSearch : onToggleClick}
							isDisabled={isDisabled}
							appearance="default"
							testId="ai-proactive-container.toggle-button"
						>
							<ToggleButtonContent
								currentStep={currentStep}
								shouldDisplay={shouldDisplay}
								resultNumber={
									UNSAFE_noExposureExp('jira-ai-similar-issues-selectable-draft-list')[0].get(
										'isEnabled',
										false,
									)
										? data?.length
										: resultNumber
								}
								error={error}
								formatMessage={formatMessage}
								messages={customMessages}
								invokedFrom={invokedFrom}
							/>
						</Button>
					)}
				</Inline>
			);
		}
	};

	const onReinitiateSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
		if (setTriggerReinitiate) {
			setTriggerReinitiate();
		}
		e.stopPropagation();
	};

	const renderHeaderSubtext = () => {
		if (error) {
			return formatMessage(messages.anErrorOccurred);
		}
		if (fg('proactive-container-noresults-fix') ? data && data.length === 0 : !data?.length) {
			return formatMessage(messages.noResultsFound);
		}
	};

	const renderChildren = () => {
		if (isError) {
			return (
				<SectionMessage appearance="warning">
					{formatMessage(customMessages.errorMessage)}
				</SectionMessage>
			);
		}
		return (
			<>
				{children}
				<AiFooter
					feedbackCollectorId={feedbackCollectorId}
					feedbackFormEntrypointId={feedbackFormEntrypointId}
					onAiFeedbackSubmittedEvent={onAiFeedbackSubmittedEvent}
					aiExperience={aiExperience}
					footerActionNode={footerActionNode}
					footerContainerStyles={footerContainerStyles}
				/>
			</>
		);
	};

	return (
		<AIContainer
			{...(isSimilarWorkItemsUIFixesEnabled(invokedFrom) ? { isBorderGrey: true } : undefined)}
			hasNewBorderExperience={isSimilarWorkItemsUIFixesEnabled(invokedFrom)}
			shouldFitContainer
			{...(isSimilarWorkItemsUIFixesEnabled(invokedFrom) ? undefined : { isShadow: false })}
			isLoading={isSimilarWorkItemsUIFixesEnabled(invokedFrom) ? false : isFetching}
			spacing="none"
			testId="ai-proactive-container.ai-container"
			ref={containerRef}
			{...(isSimilarWorkItemsUIFixesEnabled(invokedFrom) ? { borderless: true } : undefined)}
		>
			<Box
				xcss={[
					proactiveTopBarContainerStyles,
					// If we are rendering the smaller width, increase the height of the container to fit the two rows
					isSmallWidth && isErrorOrNoResults && proactiveTopBarContainerSmallWidthStyles,
				]}
			>
				{/* Render the subtext and buttons on a second line if the width is <PROACTIVE_CONTAINER_MINIMUM_WIDTH, and there is an error or no results */}
				{isSmallWidth && isErrorOrNoResults ? (
					<Stack xcss={maxWidthStyles}>
						<Inline
							space="space.050"
							testId="ai-proactive-container.inline"
							xcss={headerActionsContainerStyles}
						>
							<Box as="span" xcss={aiIconLabelStyles}>
								<PremiumIcon label={formatMessage(customMessages.headerTitle)} />
								<Text maxLines={1} color="color.text">
									{isFetching
										? formatMessage(customMessages.findingResultsMessage || messages.findingResults)
										: formatMessage(customMessages.headerTitle)}
								</Text>
								{isBeta && <Lozenge appearance="new">{formatMessage(messages.beta)}</Lozenge>}
							</Box>
							{isSimilarWorkItemsUIFixesEnabled(invokedFrom)
								? null
								: isFetching && <StreamingStatusBar hasPadding={false} />}
						</Inline>
						<Inline
							alignBlock="end"
							spread="space-between"
							xcss={subtextButtonContainerStyles}
							testId="ai-proactive-container.small-width-row"
						>
							{isErrorOrNoResults && (
								<Text as="em" color="color.text.subtlest" maxLines={1}>
									{renderHeaderSubtext()}
								</Text>
							)}
							<Inline alignBlock="center">
								{headerNode}
								{renderActionButtons()}
							</Inline>
						</Inline>
					</Stack>
				) : (
					<>
						<Inline
							space="space.050"
							testId="ai-proactive-container.inline"
							xcss={headerActionsContainerStyles}
						>
							<Box as="span" xcss={aiIconLabelStyles}>
								<PremiumIcon label={formatMessage(customMessages.headerTitle)} />
								<Text maxLines={1} color="color.text">
									{isFetching
										? formatMessage(customMessages.findingResultsMessage || messages.findingResults)
										: formatMessage(customMessages.headerTitle)}
								</Text>
								{isBeta && <Lozenge appearance="new">{formatMessage(messages.beta)}</Lozenge>}
								{isErrorOrNoResults && (
									<Text as="em" color="color.text.subtlest" maxLines={1}>
										{renderHeaderSubtext()}
									</Text>
								)}
							</Box>
							{isSimilarWorkItemsUIFixesEnabled(invokedFrom)
								? null
								: isFetching && <StreamingStatusBar hasPadding={false} />}
						</Inline>
						<Inline xcss={toggleButtonWrapperStyles}>
							{headerNode}
							{renderActionButtons()}
						</Inline>
						{isFetching && isSimilarWorkItemsUIFixesEnabled(invokedFrom) ? (
							<StreamingStatusBar hasPadding={false} />
						) : null}
					</>
				)}
			</Box>
			{shouldDisplay && renderChildren()}
		</AIContainer>
	);
};

const headerActionsContainerStyles = xcss({
	background: 'none',
	alignItems: 'center',
	marginLeft: 'space.100',
});

const aiIconLabelStyles = xcss({
	padding: 'space.025',
	paddingTop: '0',
	paddingBottom: '0',
	height: 'space.0',
	width: 'space.100',
	color: 'color.text.subtle',
	marginRight: 'space.100',
	gap: 'space.100',
	display: 'flex',
	minWidth: 'fit-content',
	alignItems: 'center',
});

const toggleButtonWrapperStyles = xcss({
	minWidth: 'fit-content',
	paddingRight: 'space.150',
	flexShrink: 0,
	alignItems: 'center',
	flexWrap: 'wrap',
});

const proactiveTopBarContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	height: 'size.400',
	borderRadius: '3px',
	width: '100%',
	paddingBlock: 'space.100',
	paddingInline: 'space.075',
});

const proactiveTopBarContainerSmallWidthStyles = xcss({
	height: '4.5rem',
});

const maxWidthStyles = xcss({
	width: '100%',
});

const subtextButtonContainerStyles = xcss({
	height: 'size.300',
	minWidth: 'fit-content',
	paddingRight: 'space.150',
	flexShrink: 0,
	flexWrap: 'wrap',
	background: 'none',
	marginLeft: 'space.100',
});
